/**
 * Multi-select filter facet: a pill button that opens a checkbox popover with
 * per-option counts. A value is "hidden" only when explicitly deselected
 * (=== false) — matching the log's isHidden() semantics.
 */
import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet, Modal } from 'react-native';
import Icon from './Icon';
import { useTheme } from '../../context/themeContext';

export interface FacetOption {
  key: string;
  label: string;
  dot?: string;
  count?: number;
}

interface FacetProps {
  label: string;
  options: FacetOption[];
  value: Record<string, boolean>; // key → false means hidden
  onChange: (next: Record<string, boolean>) => void;
}

export const Facet: React.FC<FacetProps> = ({ label, options, value, onChange }) => {
  const { theme: { colors } } = useTheme();
  const [open, setOpen] = useState(false);

  const deselected = options.filter(o => value[o.key] === false).length;
  const hasValue = deselected > 0;

  const toggle = (key: string) => {
    const next = { ...value };
    // Absent/true → visible; clicking toggles to hidden(false) and back.
    next[key] = value[key] === false;
    onChange(next);
  };
  const clear = () => {
    const next = { ...value };
    options.forEach(o => { delete next[o.key]; });
    onChange(next);
  };

  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        style={[fs.btn, { borderColor: hasValue ? colors.accentLine : colors.line, backgroundColor: hasValue ? colors.accentSoft : colors.paper }]}
      >
        {!hasValue && <Icon name="plus" size={12} color={colors.ink3} />}
        <Text style={[fs.lbl, { color: colors.ink2 }]}>{label}</Text>
        <Text style={{ color: colors.ink3, fontSize: 12 }}>{hasValue ? `${options.length - deselected}/${options.length}` : 'All'}</Text>
        <Icon name="chevronDown" size={11} color={colors.ink3} />
      </Pressable>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={fs.scrim} onPress={() => setOpen(false)}>
          <Pressable style={[fs.pop, { backgroundColor: colors.card, borderColor: colors.line }]} onPress={e => e.stopPropagation()}>
            <View style={[fs.popHead, { borderBottomColor: colors.line }]}>
              <Text style={[fs.popTitle, { color: colors.ink }]}>{label}</Text>
              {hasValue && <Pressable onPress={clear}><Text style={{ color: colors.accent, fontSize: 12 }}>Clear</Text></Pressable>}
            </View>
            {options.map(o => {
              const checked = value[o.key] !== false;
              return (
                <Pressable key={o.key} onPress={() => toggle(o.key)} style={fs.row}>
                  <View style={[fs.check, { borderColor: checked ? colors.accent : colors.lineStrong, backgroundColor: checked ? colors.accent : 'transparent' }]}>
                    {checked && <Icon name="check" size={10} color="#fff" />}
                  </View>
                  {o.dot && <View style={[fs.dot, { backgroundColor: o.dot }]} />}
                  <Text style={{ flex: 1, color: colors.ink2, fontSize: 13 }}>{o.label}</Text>
                  {o.count != null && <Text style={{ color: colors.ink4, fontSize: 12 }}>{o.count}</Text>}
                </Pressable>
              );
            })}
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
};

const fs = StyleSheet.create({
  btn: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, height: 32, borderRadius: 2, borderWidth: 1 },
  lbl: { fontSize: 12, fontWeight: '500' },
  scrim: { flex: 1, backgroundColor: 'rgba(15,23,42,0.25)', alignItems: 'center', justifyContent: 'center' },
  pop: { width: 300, maxHeight: 420, borderRadius: 2, borderWidth: 1, paddingVertical: 6 },
  popHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 12, paddingVertical: 8, borderBottomWidth: 1 },
  popTitle: { fontSize: 12, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.4 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 12, paddingVertical: 9 },
  check: { width: 16, height: 16, borderRadius: 2, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  dot: { width: 8, height: 8, borderRadius: 4 },
});
